import React from 'react';
import {ServiceBase} from "../ServiceBase";
import {IPartyJackpotService} from "./party-jackpot.service.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {PartyJackpotWinCelebrationPopupComponent} from "../../components/party-jackpot-win-celebration-popup/party-jackpot-win-celebration-popup.component";
import {IBlueRibbonPartyJackpot} from '../../../common/interfaces/blue-ribbon-party-jackpot.interface';
import {observable, action} from 'mobx';
import {IShowPartyJackpotWelcomePopupOptions} from "../../../common/interfaces/orbit-component-to-sdk-adapter/components-props/show-party-jackpot-welcome-popup-options";
import {PartyJackpotWelcomePopupComponent} from "../../components/party-jackpot-welcome-popup/party-jackpot-welcome-popup.component";
import {IModalDialogHandler} from "../dialog/dialog.service.interface";
import { isAbsolutUrl } from '../../../utils/url-utils';
import {SwiperGamesListConfiguration} from "../configurations/config-data-sections/arenas.configuration";
import {CHANNEL_ELEMENTS, CHANNEL_SOURCE} from "../../../common/const/casinofied-analytics-consts";
import {AreaType} from "@sparkware/uc-sdk-core/lib/apps/app-enums";
import { OnBlueRibbonIsEnabledChangedParams } from '../sdk/sdk-events';

export class PartyJackpotService extends ServiceBase implements IPartyJackpotService {

    constructor(_serviceFactory: IServiceFactoryExtended) {
        super(_serviceFactory);
        this._subscribe();
    }

    @observable.ref
    private _jackpots: Record<string, IBlueRibbonPartyJackpot> = {};

    private _isConnected = false;

    @observable.ref
    private _isBlueRibbonEnabled = false;

    @observable.ref
    private _isAllLiveGamesPromotion = false;

    public getPartyJackpot(partyJackpotId: string): IBlueRibbonPartyJackpot | null {
        return this._jackpots[partyJackpotId] ?? null;
    }

    public getAllLiveGamesJackpot(): IBlueRibbonPartyJackpot | null {
        const jp = Object.values(this._jackpots).find(partyJackpot => partyJackpot.supportsAllLiveGames && !partyJackpot.isExpired);
        if (jp) this.services.logger.info(`All live casino game is ${jp.jackpotId} - ${jp.name}, amount ${jp.currentValue}`);
        return jp;
    }

    // used to display component skeleton not to impact CLS
    get isAllLiveGamesPromotion(): boolean {
        return this._isBlueRibbonEnabled && this._isAllLiveGamesPromotion;
    }

    public sendAnalytics(options: {partyJackpot: IBlueRibbonPartyJackpot, action: string, addAnonymousOrReal?: boolean}) {
        let _label = "";
        if (options.addAnonymousOrReal) {
            _label = this.services.user.isAuthorized? 'Real' : 'Anonymous';
            _label += "_";
        }

        if (options.partyJackpot.name.length > 0) {
            _label += options.partyJackpot.name;
        }

        this.services.analytics.sendAnalytics("Party_Jackpot", options.action, _label);
    }

    public openJackpotInfoPage(partyJackpotId: string) {
        this.services.sdk.closeRunningGame();
        const jackpotInfo = this.getPartyJackpot(partyJackpotId);
        if (jackpotInfo && jackpotInfo.B2CLink) {
            // make sure promotion url starts with / - otherwise b2clink will be added to Orbit current page resulting a wrong promotion url
            const link = (!jackpotInfo.B2CLink.startsWith("/") && !isAbsolutUrl(jackpotInfo.B2CLink)) ? `/${jackpotInfo.B2CLink}` : jackpotInfo.B2CLink;
            this.services.location.push(link);
        }
    }

    public showPartyJackpotWelcomePopup(options: IShowPartyJackpotWelcomePopupOptions): IModalDialogHandler{
        this.services.partyJackpot.sendAnalytics({
            partyJackpot: options.partyJackpot,
            action: "View_Welcome_Message",
        });

        return this.services.dialog.showModal(dialogHandler =>
            <PartyJackpotWelcomePopupComponent
                jackpot={options.partyJackpot}
                dialogHandler={dialogHandler}
                onAcceptButtonClicked={options.onAcceptButtonClicked}
                onDeclineButtonClicked={options.onDeclineButtonClicked}
            />,
            {
                forceTransparentBackground: true,
                disableFullscreenMode: true,
                forceCentralization: true,
                mobileCenterHeader: true,
                isHighPriority: true,
                hideCloseButton: true,
                shouldBeClosedOnlyByUser: true
            }
        );
    }

    public async openPartyJackpotGamesDrawer(partyJackpotId: string): Promise<void> {
        const gamesList = this._getJackpotGameListById(partyJackpotId);

        if (gamesList?.gamesId?.length > 0) {
            await this.services.verticalGamesList.renderList(gamesList);
        } else {
            this.services.logger.info(`PartyJackpot Drawer cannot be open because list was not found`);
        }
    }

    private _subscribe() {
        this.services.sdk.events.onBlueRibbonPartyJackpotWin(this._onPartyJackpotWin);
        this.services.sdk.events.onBlueRibbonPartyJackpotStateChanged(this._onBlueRibbonPartyJackpotStateChanged);
        this.services.sdk.events.onBlueRibbonPartyJackpotInsufficientMoney(this._onPartyJackpotInsufficientMoney);
        this.services.sdk.events.onBlueRibbonConnectionComplete(this._onBlueRibbonConnectionComplete);
        this.services.sdk.events.onBlueRibbonIsEnabledChanged(this._onBlueRibbonIsEnabledChanged);
    }

    @action.bound
    private _onBlueRibbonIsEnabledChanged = (params: OnBlueRibbonIsEnabledChangedParams): void => {
        this._isBlueRibbonEnabled = params.isEnabled;
        this._isAllLiveGamesPromotion = params.isAllLiveGamesPromotion;
        if (!params.isEnabled) {
            // reset jackpots if user is not part of AB test
            this._jackpots = {};
        }
    }

    private _onBlueRibbonConnectionComplete = (): void => {
        this._isConnected = true;
    }

    private _onPartyJackpotInsufficientMoney = (partyJackpot: IBlueRibbonPartyJackpot): void => {
        this._showInsufficientMoneyToaster(partyJackpot);
    }

    @action.bound
    private _onBlueRibbonPartyJackpotStateChanged = (partyJackpot: IBlueRibbonPartyJackpot): void => {
        this._jackpots = {...this._jackpots, [partyJackpot.jackpotId]: partyJackpot};
    }

    private _onPartyJackpotWin = (partyJackpot: IBlueRibbonPartyJackpot): void => {
        if (partyJackpot.winningPlayer?.toString() === this.services.user.cid?.toString()) {
            //it's me!
            this._showPartyJackpotWinCelebrationPopup(partyJackpot);
        }else{
            this._showPartyJackpotOtherUserWinToaster(partyJackpot);
        }
    }

    private _showPartyJackpotWinCelebrationPopup(partyJackpot: IBlueRibbonPartyJackpot) {
        this.services.partyJackpot.sendAnalytics({
            partyJackpot: partyJackpot,
            action: "View_Winning_Message",
        });

        this.services.dialog.showModal(dialogHandler =>
                <PartyJackpotWinCelebrationPopupComponent
                    dialogHandler={dialogHandler}
                    jackpot={partyJackpot}
                />,
            {
                forceTransparentBackground: true,
                transparentBackgroundOpacity: 65 / 100, //65%
                forceTransparentContentBackground: true,
                disableFullscreenMode: true,
                forceCentralization: true,
                isHighPriority: true,
                hideCloseButton: true,
                shouldBeClosedOnlyByUser: true,
                hideBorders: true,
                hideDialogHeader: true,
            }
        );
    }

    private _showPartyJackpotOtherUserWinToaster(partyJackpot: IBlueRibbonPartyJackpot) {

        const toasterIcon = this.services.theme.currentTheme.images.party.toaster_icon.toString();
        const isGameRunning = this.services.sdk.isGameRunning();
        const playTextTranslation = this.services.translation.translate("PLAY");

        const titleTextTranslation = this.services.translation.translate("PARTY_JACKPOT_WIN_TOASTER_TITLE", {
            jackpotName: partyJackpot.name,
            winAmount: this.services.currency.formatMoney(partyJackpot.winAmount)
        });

        const subtitleTextTranslation = this.services.translation.translate("PARTY_JACKPOT_WIN_TOASTER_SUBTITLE", {
            startHour: partyJackpot.startHour,
            endHour: partyJackpot.endHour
        });

        const gamesList = this._getJackpotGameListById(partyJackpot.jackpotId);

        this.services.sdk.showToaster({
            MessageID: `message_${partyJackpot.jackpotId}_${partyJackpot.winAmount}`,
            Type: (isGameRunning || !gamesList?.gamesId?.length) ? 'timed_toaster' : 'action_toaster',
            Title: titleTextTranslation,
            SubTitle: subtitleTextTranslation,
            DurationInSec: 7,
            PCImage: toasterIcon,
            MobileImage: toasterIcon,
            buttonText: playTextTranslation,
            callback: () => {
                this.openPartyJackpotGamesDrawer(partyJackpot.jackpotId);
            }
        });
    }

    private _showInsufficientMoneyToaster(partyJackpot: IBlueRibbonPartyJackpot) {

        const toasterIcon = this.services.theme.currentTheme.images.party.toaster_icon.toString();
        const titleText = this.services.translation.translate("PARTY_JACKPOT_OUT_OF_CASH_TOASTER_TITLE");
        const subtitleText = this.services.translation.translate("PARTY_JACKPOT_OUT_OF_CASH_TOASTER_SUBTITLE", {
            jackpotName: partyJackpot.name,
        });
        const depositText = this.services.translation.translate("DEPOSIT");

        this.sendAnalytics({partyJackpot: partyJackpot, action: "View_No_Real_Money_Toaster"});

        this.services.sdk.showToaster({
            MessageID: `message_${partyJackpot.jackpotId}_no_money`,
            Type: 'action_toaster',
            Title: titleText,
            SubTitle: subtitleText,
            DurationInSec: 7,
            PCImage: toasterIcon,
            MobileImage: toasterIcon,
            buttonText: depositText,
            callback: async () => {
                this.sendAnalytics({partyJackpot: partyJackpot, action: "Deposit_No_Real_Money_Toaster"});
                this.services.sdk.closeRunningGame();
                await this.services.cashier.openDeposit({
                    channel: {
                        source: CHANNEL_SOURCE.partyJackpotToaster,
                        area: AreaType.Message,
                        element: CHANNEL_ELEMENTS.depositButton
                    }
                });
            }
        });
    }

    private _getJackpotGameListById(partyJackpotId: string): SwiperGamesListConfiguration {
        return this.services.configuration.getGamesListByJackpotId(partyJackpotId);
    }
}
